import { useState, useEffect } from "react";

const CALENDLY_URL = "https://calendly.com/hello-krystaldaniellecoaching/1hr";

const NAV_LINKS = [
  { id: "for-you", label: "Who It's For" },
  { id: "gain",    label: "What You'll Gain" },
  { id: "about",   label: "About Me" },
];

const scrollTo = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Navbar() {
  const [scrolled, setScrolled]     = useState(false);
  const [menuOpen, setMenuOpen]     = useState(false);
  const [activeId, setActiveId]     = useState<string>("");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    NAV_LINKS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveId(id);
        },
        { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
      );

      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/95 backdrop-blur-sm border-b border-sand/40 py-4" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
        {/* Desktop Nav Links - Left */}
        <div className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="group relative font-sans text-sm tracking-widest uppercase cursor-pointer whitespace-nowrap bg-transparent border-none transition-colors duration-300 pb-1"
              style={{ color: activeId === id ? "#B5714A" : "#1C1C1A" }}
            >
              {label}
              {/* Animated underline */}
              <span
                className="absolute bottom-0 left-0 h-px bg-accent transition-all duration-400 ease-out"
                style={{ width: activeId === id ? "100%" : "0%" }}
              />
            </button>
          ))}
        </div>

        {/* Book a Session - Right (Desktop) */}
        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="nofollow noreferrer"
          className="hidden md:inline-block font-sans text-sm tracking-widest uppercase px-6 py-3 border border-ink text-ink hover:bg-ink hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          Reserve Your Clarity Session
        </a>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`ri-${menuOpen ? "close" : "menu"}-line text-xl text-ink`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-sand/40 px-6 pt-6 pb-8 flex flex-col gap-0">
          {NAV_LINKS.map(({ id, label }) => (
            <button
              key={id}
              className="font-sans text-sm tracking-widest uppercase py-4 border-b border-sand/30 cursor-pointer text-left bg-transparent border-x-0 border-t-0 w-full transition-colors duration-300"
              style={{ color: activeId === id ? "#B5714A" : "#1C1C1A" }}
              onClick={() => { scrollTo(id); setMenuOpen(false); }}
            >
              {label}
            </button>
          ))}
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="nofollow noreferrer"
            className="font-sans text-sm tracking-widest uppercase mt-6 px-6 py-3 border border-ink text-ink text-center cursor-pointer whitespace-nowrap"
          >
            Reserve Your Clarity Session
          </a>
        </div>
      )}
    </nav>
  );
}
